/**
 * Alert display helpers (Story 27.2) — shared by AlertTableView, AlertCardGrid,
 * and AlertFormDialog so the readable condition / interval badge / platform
 * label render identically everywhere.
 */

import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import type { AlertCondition, AlertInterval } from "./types";

/** The readable comparison symbol/phrase for each condition (epics.md AC). */
export const CONDITION_SYMBOL: Record<AlertCondition, string> = {
  gt: ">",
  lt: "<",
  gte: "≥",
  lte: "≤",
  change_up: "↑ increased by",
  change_down: "↓ decreased by",
};

/** The condition dropdown option labels (create/edit dialog). */
export const CONDITION_OPTIONS: { value: AlertCondition; label: string }[] = [
  { value: "gt", label: "> (greater than)" },
  { value: "lt", label: "< (less than)" },
  { value: "gte", label: "≥ (greater than or equal)" },
  { value: "lte", label: "≤ (less than or equal)" },
  { value: "change_up", label: "↑ increased by" },
  { value: "change_down", label: "↓ decreased by" },
];

export const INTERVAL_OPTIONS: { value: AlertInterval; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

/** "DAY" / "WEEK" / "MONTH" badge label. */
export function intervalBadge(interval: AlertInterval): string {
  return interval.toUpperCase();
}

/** Human platform name (e.g. "Google Ads"), falling back to the raw slug. */
export function platformDisplayName(platform: string): string {
  return (
    PICKER_PLATFORMS.find((p) => p.platform === platform)?.displayName ??
    platform
  );
}

/** Platform logo URL via the integration registry, or undefined. */
export function platformLogoUrl(platform: string): string | undefined {
  const meta = PICKER_PLATFORMS.find((p) => p.platform === platform);
  return meta?.domain ? logoUrl(meta.domain) : undefined;
}

/**
 * Humanize a registry metric key for display, e.g. "bounceRate" → "Bounce
 * Rate", "cost" → "Cost". A small local copy (the canonical humanizeMetricKey
 * lives in the report editor and would pull editor deps into the alerts
 * bundle/tests). Splits camelCase + snake/kebab, title-cases each word.
 */
export function humanizeMetricKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Compact threshold formatter — change_up/change_down thresholds are percents,
 * absolute conditions render the bare number with thousands separators.
 */
export function formatThreshold(
  condition: AlertCondition,
  threshold: number,
): string {
  const n = threshold.toLocaleString("en-US");
  return condition === "change_up" || condition === "change_down"
    ? `${n}%`
    : n;
}

/**
 * The readable condition clause, e.g. "Cost > 2,000" or
 * "Sessions ↓ decreased by 20%". `metricLabel` is the humanized metric name.
 */
export function readableCondition(
  metricLabel: string,
  condition: AlertCondition,
  threshold: number,
): string {
  return `${metricLabel} ${CONDITION_SYMBOL[condition]} ${formatThreshold(condition, threshold)}`;
}

/** "Never" or a short locale date for the last-triggered column. */
export function lastTriggeredLabel(ms: number | null): string {
  if (ms === null) return "Never";
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * "Recently triggered" = fired within the last 48h. Drives the subtle
 * highlight/badge (epics.md AC: recently-triggered alerts show a subtle
 * indicator).
 */
export function isRecentlyTriggered(ms: number | null): boolean {
  if (ms === null) return false;
  return Date.now() - ms < 48 * 60 * 60 * 1000;
}
